import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

interface MenuItem {
  label: string;
  href: string;
  children?: MenuItem[];
}

const leftMenu: MenuItem[] = [
  { label: "Airport Shuttle", href: "/airport-shuttle" },
  { label: "About", href: "/about" },
];

const rightMenu: MenuItem[] = [
  { label: "Careers", href: "/careers" },
  { label: "Contact Us", href: "/contact-us" },
];

function DropdownMenu({ items, isOpen }: { items: MenuItem[]; isOpen: boolean }) {
  if (!isOpen) return null;
  return (
    <ul className="absolute top-full left-0 min-w-[260px] bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 z-50 mt-1">
      {items.map((item) => (
        <li key={item.href}>
          <Link
            to={item.href}
            className="block px-5 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50 transition-colors"
          >
            {item.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NavItem({ item }: { item: MenuItem }) {
  const [open, setOpen] = useState(false);
  const hasChildren = !!item.children;
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!hasChildren) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [hasChildren]);

  return (
    <li
      ref={ref}
      className="relative"
      onMouseEnter={() => hasChildren && setOpen(true)}
      onMouseLeave={() => hasChildren && setOpen(false)}
    >
      <Link
        to={item.href}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors"
        onClick={() => !hasChildren && setOpen(false)}
      >
        <span className="whitespace-nowrap">{item.label}</span>
        {hasChildren && (
          <i className="ri-arrow-down-s-line text-xs" />
        )}
      </Link>
      {hasChildren && item.children && (
        <DropdownMenu items={item.children} isOpen={open} />
      )}
    </li>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const allMobileItems = [
    { label: "Home", href: "/" },
    ...leftMenu,
    ...rightMenu,
  ];

  return (
    <header
      className={`w-full transition-all duration-300 ${
        scrolled
          ? "fixed top-0 left-0 z-40 bg-background-50/95 backdrop-blur-sm shadow-sm"
          : "relative z-40 bg-background-50"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center justify-between h-20">
          {/* Left Menu */}
          <nav className="flex items-center gap-1 flex-1 justify-end pr-6">
            <ul className="flex items-center gap-1">
              {leftMenu.map((item) => (
                <NavItem key={item.label} item={item} />
              ))}
            </ul>
          </nav>

          {/* Center Logo */}
          <Link to="/" className="shrink-0 mx-4">
            <img
                          src="/images/logo.png"
              alt="Southern Transit LLC"
              width={97}
              height={100}
              className="w-[97px] h-auto"
            />
          </Link>

          {/* Right Menu */}
          <nav className="flex items-center gap-1 flex-1 justify-start pl-6">
            <ul className="flex items-center gap-1">
              {rightMenu.map((item) => (
                <NavItem key={item.label} item={item} />
              ))}
            </ul>
          </nav>
        </div>

        {/* Mobile Navigation */}
        <div className="flex lg:hidden items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img
                          src="/images/logo.png"
              alt="Southern Transit LLC"
              width={48}
              height={50}
              className="w-12 h-auto"
            />
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/airport-shuttle"
              className="hidden sm:inline-flex items-center px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              View Schedules
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-primary-600 transition-colors"
              aria-label="Toggle navigation"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-2xl" />
              ) : (
                <i className="ri-menu-line text-2xl" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-background-50 z-50 overflow-y-auto">
          <nav className="px-4 py-6">
            <ul className="space-y-1">
              {allMobileItems.map((item) => {
                const hasChildren = !!item.children;
                return (
                  <li key={item.label} className="border-b border-background-200/70 last:border-0">
                    <div className="py-3">
                      <Link
                        to={item.href}
                        className="text-base font-medium text-foreground-800 hover:text-primary-600 transition-colors"
                        onClick={() => !hasChildren && setMobileOpen(false)}
                      >
                        {item.label}
                      </Link>
                      {hasChildren && item.children && (
                        <ul className="mt-2 ml-4 space-y-1">
                          {item.children.map((child) => (
                            <li key={child.href}>
                              <Link
                                to={child.href}
                                className="block py-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                                onClick={() => setMobileOpen(false)}
                              >
                                {child.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
}